import { escapeHtml, opaque } from "./render.js";
import { expandRegion, findRegion } from "./sections.js";
/** Unwrap `<![CDATA[ … ]]>` sections with a linear scan (no ReDoS-prone regex). */
function stripCdata(s) {
    const open = "<![CDATA[";
    const close = "]]>";
    let out = "";
    let i = 0;
    for (;;) {
        const start = s.indexOf(open, i);
        if (start < 0)
            return out + s.slice(i);
        out += s.slice(i, start);
        const end = s.indexOf(close, start + open.length);
        if (end < 0)
            return out + s.slice(start + open.length);
        out += s.slice(start + open.length, end);
        i = end + close.length;
    }
}
function decodeEntities(s) {
    return stripCdata(s)
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&apos;/g, "'")
        .replace(/&amp;/g, "&")
        .trim();
}
/**
 * Inner text of each `<name …> … </name>` block, found with a linear scan.
 *
 * A regex like `<name(?:\s[^>]*)?>([\s\S]*?)</name>` is QUADRATIC when the body
 * contains many opening tags and no closer: 256 KiB of `<item>` took 5.4s, and
 * the 5 MiB fetch cap extrapolates to minutes of pegged CPU (#173). indexOf
 * scanning is O(n) — the same technique already used by stripCdata.
 */
function scanBlocks(xml, name, limit = Infinity) {
    const out = [];
    const hay = xml.toLowerCase();
    const n = name.toLowerCase();
    const open = `<${n}`;
    const close = `</${n}`;
    let i = 0;
    while (out.length < limit) {
        const s = hay.indexOf(open, i);
        if (s < 0)
            break;
        // The next char must end the tag name, so `<item>`/`<item a="b">` match but
        // `<items>` does not.
        const c = hay.charCodeAt(s + open.length);
        if (!(c === 0x3e || c === 0x20 || c === 0x09 || c === 0x0a || c === 0x0d)) {
            i = s + open.length;
            continue;
        }
        const gt = hay.indexOf(">", s + open.length);
        if (gt < 0)
            break;
        if (hay.charCodeAt(gt - 1) === 0x2f) {
            i = gt + 1; // self-closing `<item/>` has no inner text
            continue;
        }
        const e = hay.indexOf(close, gt + 1);
        if (e < 0)
            break; // unclosed — nothing further can match
        out.push(xml.slice(gt + 1, e));
        const endGt = hay.indexOf(">", e + close.length);
        i = endGt < 0 ? e + close.length : endGt + 1;
    }
    return out;
}
function tag(block, name) {
    const first = scanBlocks(block, name, 1)[0];
    return first === undefined ? undefined : decodeEntities(first);
}
function blocksBetween(xml, name) {
    return scanBlocks(xml, name);
}
/**
 * The item image, from whichever of the three conventions the publisher uses.
 *
 * A linear scan for the attribute rather than a global regex, for the same
 * ReDoS reason as everything else in this file. Order is deliberate:
 * `<enclosure>` is the RSS 2.0 standard, `media:content` the Media RSS one, and
 * `media:thumbnail` a smaller fallback — a publisher emitting several means the
 * first is the full-size one.
 *
 * Without this a feed edition has no image at all, and the story regions in a
 * designed template have an `{{IMG}}` slot with nothing to put in it.
 */
function itemImage(item) {
    for (const tagName of ["enclosure", "media:content", "media:thumbnail"]) {
        const lower = item.toLowerCase();
        let from = 0;
        for (;;) {
            const at = lower.indexOf(`<${tagName}`, from);
            if (at === -1)
                break;
            const end = item.indexOf(">", at);
            if (end === -1)
                break;
            const el = item.slice(at, end);
            // enclosure uses `url`, media:* uses `url` too, but be tolerant of both.
            const m = /\b(?:url|src)\s*=\s*("([^"]*)"|'([^']*)')/i.exec(el);
            const url = m?.[2] ?? m?.[3];
            if (url) {
                // An enclosure may be audio or a PDF; only take it when it looks like
                // an image, or when the publisher gave no type at all.
                const type = /\btype\s*=\s*("([^"]*)"|'([^']*)')/i.exec(el);
                const t = (type?.[2] ?? type?.[3] ?? "").toLowerCase();
                if (t === "" || t.startsWith("image/"))
                    return decodeEntities(url);
            }
            from = end + 1;
        }
    }
    return undefined;
}
function parseRss(xml) {
    return blocksBetween(xml, "item").map((item) => ({
        title: tag(item, "title"),
        link: tag(item, "link"),
        content: tag(item, "description") ?? tag(item, "content:encoded"),
        published: tag(item, "pubDate"),
        id: tag(item, "guid") ?? tag(item, "link"),
        ...(itemImage(item) ? { image: itemImage(item) } : {}),
    }));
}
/** Extract the Atom `<link href="…"/>` value without a ReDoS-prone global regex. */
function atomLinkHref(entry) {
    const lt = entry.toLowerCase().indexOf("<link");
    if (lt < 0)
        return undefined;
    const gt = entry.indexOf(">", lt);
    const tag = gt >= 0 ? entry.slice(lt, gt + 1) : entry.slice(lt);
    const m = tag.match(/\bhref\s*=\s*(?:"([^"]*)"|'([^']*)')/i);
    return m ? (m[1] ?? m[2] ?? undefined) : undefined;
}
function parseAtom(xml) {
    return blocksBetween(xml, "entry").map((entry) => {
        // Atom link is an attribute: <link href="..."/>
        const href = atomLinkHref(entry);
        return {
            title: tag(entry, "title"),
            link: href ? decodeEntities(href) : undefined,
            content: tag(entry, "summary") ?? tag(entry, "content"),
            published: tag(entry, "updated") ?? tag(entry, "published"),
            id: tag(entry, "id"),
        };
    });
}
function parseJsonFeed(body) {
    const doc = JSON.parse(body);
    const items = doc.items ?? [];
    return items.map((it) => ({
        title: typeof it["title"] === "string" ? it["title"] : undefined,
        link: typeof it["url"] === "string" ? it["url"] : undefined,
        content: typeof it["content_html"] === "string"
            ? it["content_html"]
            : typeof it["content_text"] === "string"
                ? it["content_text"]
                : undefined,
        published: typeof it["date_published"] === "string" ? it["date_published"] : undefined,
        ...(typeof it["_newsletter_subject"] === "string" ? { newsletterSubject: it["_newsletter_subject"] } : {}),
        image: typeof it["image"] === "string" ? it["image"] : undefined,
        id: typeof it["id"] === "string" ? it["id"] : undefined,
    }));
}
export function parseFeed(body, format) {
    switch (format) {
        case "rss":
            return parseRss(body);
        case "atom":
            return parseAtom(body);
        case "json":
            return parseJsonFeed(body);
    }
}
/** Map a feed item's fields onto merge-tag names per the feed's fieldMap. */
export function mapFeedItem(item, fieldMap) {
    const out = {};
    for (const [field, tagName] of Object.entries(fieldMap)) {
        const value = item[field];
        if (value !== undefined)
            out[tagName] = value;
    }
    return out;
}
/**
 * Assemble a fresh edition from the latest feed items: the lead item's title
 * becomes the subject, and up to `maxItems` items become editorial blocks (only
 * items with both a title and a link). `editionKey` (e.g. the firing date)
 * makes the edition id stable and idempotent for that firing.
 */
export function buildEdition(items, opts) {
    const usable = items.filter((i) => i.title && i.link);
    const chosen = usable.slice(0, opts.maxItems ?? 10);
    const lead = chosen[0]?.title ?? "Your newsletter";
    const subject = opts.subjectPrefix ? `${opts.subjectPrefix}${lead}` : lead;
    const blocks = chosen.map((i) => ({
        kind: "editorial",
        label: i.title,
        url: i.link,
    }));
    // Feed mappings are per-campaign values: the lead item is the stable source
    // for placeholders such as {{article_excerpt}} in the generated edition.
    // Keep the generated editorial links as well; mapped fields supplement them.
    const mapped = mapFeedItem(chosen[0] ?? {}, opts.fieldMap ?? {});
    const mappedTags = Object.keys(mapped).filter((tag) => tag !== (opts.fieldMap ?? {}).link);
    if (mappedTags.length > 0) {
        blocks.unshift({ kind: "text", html: mappedTags.map((tag) => `{{${tag}}}`).join("\n") });
    }
    return {
        editionId: `${opts.baseCampaignId}-${opts.editionKey}`,
        subject,
        template: { blocks },
    };
}
/**
 * Split a feed url that may name several sources (#309).
 *
 * A publication draws its main section from more than one feed — news, sport,
 * obituaries — and wants them in a single story sequence, so the stored url is
 * a comma-separated list. Order is the publisher's editorial decision and is
 * preserved: the first source's lead item names the edition.
 */
export function splitFeedUrls(url) {
    return url
        .split(",")
        .map((u) => u.trim())
        .filter((u) => u.length > 0);
}
/** Feed items shaped for a story region. */
function toStories(items) {
    return items
        .filter((i) => i.title && i.link)
        .map((i) => ({
        headline: i.title,
        url: i.link,
        ...(i.image ? { image: i.image } : {}),
        ...(i.content ? { bodyText: stripTags(i.content) } : {}),
    }));
}
/** Feed `content` is publisher HTML; story regions supply their own markup. */
function stripTags(html) {
    return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}
/**
 * The wrapper the legacy pipeline put around every interleaved ad: a divider,
 * the word "Advertisement", the creative, a divider. The disclosure has to be
 * there whatever the creative is, so it lives here rather than in each tag.
 */
const AD_WRAPPER = '<table align="center" border="0" cellpadding="0" cellspacing="0" width="600" ' +
    'style="margin:0 auto;max-width:600px;"><tr><td style="border-top:1px solid #ddd;' +
    'font-size:1px;line-height:1px;">&nbsp;</td></tr><tr><td align="center" ' +
    'style="font-family:Arial,sans-serif;font-size:11px;color:#999;padding:6px 0;">' +
    "Advertisement</td></tr><tr><td align=\"center\">{{ADPLACEMENT}}</td></tr>" +
    '<tr><td style="border-top:1px solid #ddd;font-size:1px;line-height:1px;">&nbsp;</td></tr></table>';
/**
 * Document-level tokens the template may declare.
 *
 * Any left unfilled are cleared, so an operator who deletes the ad-tag config
 * or leaves a section unsourced gets a blank rather than `{{CATEGORYONENICENAME}}`
 * in the inbox. Deliberately an explicit list, NOT a `[A-Z_]+` sweep: merge
 * tags are lowercase and are resolved per recipient much later, so a blanket
 * pass would delete `{{unsubscribe_url}}` before it was ever substituted.
 */
const DOCUMENT_TOKENS = [
    "PREHEADER",
    "NEWSLETTERNAME",
    "CATEGORYONENICENAME",
    "CATEGORYTWONICENAME",
    "MARQUEEAD",
    "SAFERTB",
    "CONTENT",
];
/** Region names, in the order the shipped template declares them. */
const SECTIONS = {
    main: { lead: "MAIN STORY", item: "BODY STORY" },
    categoryOne: { item: "NEWSLETTER_AFTERMAINONE_STORIES", maxItems: 3 },
    categoryTwo: { item: "NEWSLETTER_AFTERMAINTWO_STORIES", maxItems: 2 },
};
/**
 * Does this template compose feed items INTO itself, rather than being replaced
 * by them?
 *
 * The presence of a main story region is the opt-in. A template without one
 * keeps the old `buildEdition` behaviour, so every series scheduled before this
 * is unaffected — and a feed series carrying a composed body that has never
 * been sent does not suddenly start mailing it.
 */
export function hasStoryRegions(t) {
    return t.html != null && findRegion(t.html, SECTIONS.main.lead) !== undefined;
}
/**
 * Detect the ad positions a template's HTML offers.
 *
 * The send only fills a marker that exists, so a slot set for a position the
 * template lacks is dropped without a word. Reading the positions off the HTML
 * lets the console show them and refuse a slot with nowhere to go. Body ads
 * need both the main lead and its repeating item region, as the expansion does.
 */
export function adPositions(html) {
    return {
        marquee: html.includes("{{MARQUEEAD}}"),
        footer: html.includes("{{SAFERTB}}"),
        body: findRegion(html, SECTIONS.main.lead) !== undefined && findRegion(html, SECTIONS.main.item) !== undefined,
    };
}
/**
 * Why a series' ad slots cannot all be placed in this HTML, or undefined.
 * One sentence per missing position, naming the marker to add.
 */
export function adSlotPlacementProblem(html, slots) {
    const at = adPositions(html);
    const problems = [];
    if (slots.marqueeSlot && !at.marquee)
        problems.push("a Marquee slot is set but the template has no {{MARQUEEAD}} marker");
    if (slots.footerSlot && !at.footer)
        problems.push("a Footer slot is set but the template has no {{SAFERTB}} marker");
    if (slots.bodySlots?.length && !at.body)
        problems.push("Body slots are set but the template has no MAIN STORY / BODY STORY regions");
    return problems.length ? problems.join("; ") : undefined;
}
/**
 * Expand a designed template's story regions from the current feed (#299).
 *
 * The template is the document and the feed supplies its contents — the inverse
 * of `buildEdition`, which builds a body out of feed items and discards
 * whatever the operator composed. Everything outside a region survives byte for
 * byte: masthead, sponsor banner, section headings, tip callout, footer.
 */
export function composeFromRegions(html, stories, ads, 
/** Document-level values: newsletter name, section headings, preheader. */
labels, 
/** Per-edition placement id for ad tags (#301). */
placement, 
/** Segment id the ad tags key on. */
listId) {
    // A bare array fills the main run only. The category sections are separately
    // sourced — in the legacy pipeline they were WordPress category queries, not
    // the main feed — so filling them from the same items would print the same
    // stories three times under different headings.
    const byRegion = Array.isArray(stories) ? { main: stories } : stories;
    let out = html;
    // Rightmost region first, so earlier spans keep their indices.
    const plans = [
        { region: SECTIONS.categoryTwo, stories: byRegion.categoryTwo ?? [], ads: undefined },
        { region: SECTIONS.categoryOne, stories: byRegion.categoryOne ?? [], ads: undefined },
        { region: SECTIONS.main, stories: byRegion.main ?? [], ads: ads?.body },
    ];
    for (const plan of plans) {
        const itemName = plan.region.item;
        const leadName = "lead" in plan.region ? plan.region.lead : undefined;
        const item = findRegion(out, itemName);
        if (!item)
            continue;
        const lead = leadName ? findRegion(out, leadName) : undefined;
        // The lead and item regions are adjacent siblings, so the span to replace
        // runs from the start of whichever comes first to the end of the other.
        const start = lead ? Math.min(lead.start, item.start) : item.start;
        const end = lead ? Math.max(lead.end, item.end) : item.end;
        const expanded = expandRegion({ ...(lead ? { lead: lead.inner } : {}), item: item.inner }, plan.stories, {
            // Ad html is third-party and must not be rewritten by the anchor
            // scanner — see #313. Generated story markup IS ours and stays tracked.
            ...(plan.ads ? { ads: plan.ads.map(opaque), adWrapper: AD_WRAPPER } : {}),
            ...("maxItems" in plan.region ? { maxItems: plan.region.maxItems } : {}),
        });
        out = out.slice(0, start) + expanded + out.slice(end);
    }
    // Document-level placements, after the regions so a marker inside a story
    // region cannot swallow them.
    out = out.split("{{MARQUEEAD}}").join(ads?.marquee ? opaque(ads.marquee) : "");
    out = out.split("{{SAFERTB}}").join(ads?.footer ? opaque(ads.footer) : "");
    // Ad-tag substitutions the ad server expects (#301). Despite the brace
    // syntax these are resolved HERE, not by the ESP — `{{Address}}` is the one
    // token that passes through to per-recipient templating, and it is
    // deliberately left alone.
    if (placement) {
        out = out.split("{{MessageVersionInstance.Id}}").join(placement);
    }
    if (listId) {
        out = out.split("{LIST_ID}").join(listId);
    }
    for (const [token, value] of Object.entries(labels ?? {})) {
        out = out.split(`{{${token}}}`).join(escapeHtml(value));
    }
    // An unsupplied label leaves an empty heading rather than a literal
    // `{{CATEGORYONENICENAME}}` in the inbox — a visible placeholder is worse
    // than a blank.
    //
    // Scoped to the template's OWN token vocabulary rather than anything
    // uppercase. Merge tags are lowercase by convention (`unsubscribe_url`,
    // `first_name`) and are resolved per recipient much later, so a blanket
    // sweep here would delete them before they were ever substituted — the
    // unsubscribe link included.
    for (const token of DOCUMENT_TOKENS) {
        out = out.split(`{{${token}}}`).join("");
    }
    return out;
}
export function planLaunchDescriptor(payload, items, fresh) {
    // Current content wins over the frozen payload, field by field. Absent
    // entirely (a schedule made before #298, or one whose body was removed) it
    // falls through to the snapshot, so legacy schedules behave exactly as they
    // did — which is what makes this safe to ship before every schedule has one.
    const base = {
        ...payload.descriptor,
        ...(fresh?.subject ? { subject: fresh.subject } : {}),
        ...(fresh?.previewText ? { previewText: fresh.previewText } : {}),
        ...(fresh?.template ? { template: fresh.template } : {}),
    };
    if (payload.feed && items) {
        // A designed template composes the feed INTO itself (#299): the operator's
        // masthead, sponsor banner, section headings and footer all survive, and
        // only the marked story regions are expanded. Opt-in on the template
        // carrying a main story region, so a series scheduled before this — or one
        // whose composed body has never been reviewed — keeps the old behaviour.
        if (base.template.html != null && hasStoryRegions(base.template)) {
            const stories = toStories(items);
            return {
                ...base,
                campaignId: `${payload.descriptor.campaignId}-${payload.editionKey}`,
                // The lead story still names the edition: a daily newsletter's subject
                // IS its lead headline.
                subject: fresh?.subjectMode === "fixed" ? base.subject : (stories[0]?.headline ?? base.subject),
                template: { html: composeFromRegions(base.template.html, fresh?.sections ? {
                        main: toStories(fresh.sections.main), categoryOne: toStories(fresh.sections.categoryOne ?? []), categoryTwo: toStories(fresh.sections.categoryTwo ?? []),
                    } : stories, fresh?.ads, fresh?.documentValues, fresh?.placement, fresh?.adAudienceId ?? base.segmentId ?? base.listId) },
                campaignAttributes: mapFeedItem(items[0] ?? {}, payload.feed.fieldMap ?? {}),
            };
        }
        const edition = buildEdition(items, {
            baseCampaignId: payload.descriptor.campaignId,
            editionKey: payload.editionKey,
            fieldMap: payload.feed.fieldMap,
        });
        return {
            ...base,
            campaignId: edition.editionId,
            // The feed still names the edition: a daily newsletter's subject is its
            // lead story, not a fixed line the operator set months ago. An operator
            // who wants a fixed subject is asking for a different feature.
            subject: fresh?.subjectMode === "fixed" ? base.subject : edition.subject,
            template: edition.template,
            campaignAttributes: mapFeedItem(items[0] ?? {}, payload.feed.fieldMap ?? {}),
        };
    }
    return {
        ...base,
        campaignId: `${payload.descriptor.campaignId}-${payload.editionKey}`,
    };
}
//# sourceMappingURL=feed.js.map